// Motion, and the restraint that makes it feel fast rather than decorated.
//
// Two rules govern everything here.
//
// Motion must never DELAY anything. An animation that runs before content is
// usable is lag wearing a costume — so entrances animate what has already
// arrived, and nothing waits on a transition to finish before becoming
// interactive.
//
// And it must be honest about what it signals. A fade means "this is new";
// a shift means "this moved". Using them the other way round makes an interface
// feel arbitrary even when every duration is right.

/** Somebody who asked their operating system for less motion gets none. */
export function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true
  );
}

/**
 * Run a DOM update as a cross-fade where the browser supports it.
 *
 * Deliberately awaits `updateCallbackDone` — when the DOM is updated — rather
 * than `finished`, which resolves only after the animation plays out. Waiting
 * for the animation would make navigation measurably slower than not animating
 * at all, which is the exact trap this file exists to avoid.
 */
export async function withTransition(update: () => void | Promise<void>): Promise<void> {
  if (prefersReducedMotion() || typeof document.startViewTransition !== "function") {
    await update();
    return;
  }
  await document.startViewTransition(update).updateCallbackDone;
}

/**
 * Stagger the entrance of a group of elements.
 *
 * Capped deliberately: a long list staggered item-by-item turns into a queue
 * the reader has to wait through. After the cap everything arrives together,
 * which reads as one movement rather than a slow one.
 */
export function stagger(
  elements: Iterable<Element>,
  { step = 40, cap = 6, distance = 8 } = {},
): void {
  if (prefersReducedMotion()) return;
  let i = 0;
  for (const el of elements) {
    const delay = Math.min(i, cap) * step;
    (el as HTMLElement).animate(
      [
        { opacity: 0, transform: `translateY(${distance}px)` },
        { opacity: 1, transform: "none" },
      ],
      {
        duration: 260,
        delay,
        // Decelerating: fast to start, settling at the end. Matches how a
        // thrown object arrives, which is why it reads as physical rather than
        // as a timer expiring.
        easing: "cubic-bezier(0.2, 0.7, 0.3, 1)",
        fill: "backwards",
      },
    );
    i++;
  }
}

/** Fade a single element in — for content that arrives after first paint. */
export function fadeIn(el: Element | null, distance = 6): void {
  if (!el || prefersReducedMotion()) return;
  (el as HTMLElement).animate(
    [
      { opacity: 0, transform: `translateY(${distance}px)` },
      { opacity: 1, transform: "none" },
    ],
    { duration: 220, easing: "cubic-bezier(0.2, 0.7, 0.3, 1)", fill: "backwards" },
  );
}
